import {
	useState,
	useContext,
	useMemo,
	useRef,
	useEffect,
	RefObject,
	useCallback,
	memo,
} from 'react';
import { useSearchParams, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Box } from 'src/components/ui';
import CampaignSection from 'src/components/campaigns/CampaignSection';
import SummaryExtraInformation from '../../components/campaigns/SummaryExtraInfo';
import { GatherInfoForm } from 'src/components/common/LazyComponents';
import useAccountConfigOptions from 'src/hooks/config/useAccountConfigOptions';
import {
	generateDesignDirectionsFromLayers,
	generateDesignDirectionsWithTemplates,
	getInterpretations,
} from 'src/services/campaign';
import { regenerateCampaignDesignDirection } from 'src/services/campaign';
import {
	AppInputConfigContext,
	CampaignContext,
	withAppConfigProvider,
	AccountConfigContext,
} from 'src/contexts';
import useCampaignConfig from 'src/hooks/useCampaignConfig';
import useBudgetManager from 'src/hooks/useBudgetManager';
import { IOption } from 'src/lib/schemas/misc';
import { toastError } from 'src/services/toast';
import {
	createOrUpdateCampaign,
	genrateCampaignCreatives,
} from 'src/services/campaign';
import UserContext from 'src/contexts/user/UserContext';
import { getAvailableChannels } from 'src/services/account';
import {
	ICampaign,
	ICampaignSchedule,
	IChannelCreativeAttributes,
	IChannelGroup,
} from 'src/lib/schemas';
import TemplateContext from 'src/contexts/templates/TemplatesContext';
import DesignDirections from 'src/components/campaigns/design-directions/DesignDirections';
import CampaignSchedule from 'src/components/campaigns/schedule/CampaignSchedule';
import { generateBudgetBreakdownByPlacements } from 'src/components/campaign/CampaignBudget/parts/utils';
import CampaignStepper from 'src/components/campaigns/CampaignStepper';
import { withCampaignProvider } from 'src/contexts';
import { validateCampaignConfig } from 'src/components/campaigns/utils/validations';
import FusionLoading from 'src/components/common/FusionLoading';
import { CampaignHeader } from 'src/components/campaign/parts';
import { cloneDeepWith, isEqual, omit } from 'lodash';
import {
	ICreative,
	IDesignDirection,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import { ICampaignNavigationEvent, IEvent } from 'src/lib/schemas/event/event';
import { processCallback } from 'src/lib/utils/processCallback';
import { unstable_batchedUpdates } from 'react-dom';
import { useOnboarding } from 'src/contexts/onboarding';
import { CampaignWizardModal, CampaignStepsOverlay, CampaignFormTour, DesignsTour, DESIGNS_TOUR_STEPS, FinalizeTour, FINALIZE_TOUR_STEPS } from 'src/components/onboarding';
import useCopilotChatContext from 'src/fusion-copilot/hooks/useCopilotChatContext';
import { useRightSidebarContext } from 'src/contexts/app/RightSidebarContext';
import { useCatalogsContext } from 'src/contexts/catalogs/CatalogsContext';

interface CustomWindow extends Window {
	fusion?: any;
}
declare const window: CustomWindow;

const CreateEditCampaign = memo(() => {
	// Add render counter for debugging
	const renderCount = useRef(0);
	renderCount.current += 1;

	// Add safety wrapper to prevent crashes
	try {
		const [searchParams] = useSearchParams();
		const fromCopilot = searchParams.get('from') === 'copilot';
		// Query params for deep-linking from chat to edit a specific DD
		const editDDId = searchParams.get('dd');
		const returnToChat = searchParams.get('returnTo') === 'chat';

		const {
			campaign,
			id: campaignId,
			setCampaign,
			refetchCampaign,
			enabledSections,
			openSections,
			setOpenSections,
			oldCampaignCopy,
			setOldCampaignCopy,
			setWasCampaignChanged,
			isFetching: isCampaignFetching,
		} = useContext(CampaignContext);

		const { updateBudget } = useBudgetManager();

		// ALL HOOKS MUST BE CALLED HERE, BEFORE ANY CONDITIONAL LOGIC
		const [group, setGroup] = useState<string | undefined>();
		const [campaignTitle, setCampaignTitle] = useState('');
		const [metaOptions, setMetaOptions] = useState<IOption[]>([]);
		const [availableChannelGroups, setAvailableChannelGroups] = useState<
			IChannelGroup[] | null
		>(null);
		const [availableCreatives, setAvailableCreatives] =
			useState<IChannelCreativeAttributes[]>();
		const [isLoadingChannels, setIsLoadingChannels] = useState(false);
		const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
		const [designDirections, setDesignDirections] = useState(
			campaign?.designDirections ?? ([] as Array<IDesignDirection>),
		);
		const [creatives, setCreatives] = useState(
			campaign?.creatives ?? ([] as Array<ICreative>),
		);
		const [isGeneratingDesignDirections, setIsGeneratingDesignDirections] =
			useState(false);

		// REMOVED: local isLoading state - using isCampaignFetching from context instead
		const designDirectionsRef = useRef<HTMLDivElement>(null);
		const gatherInfoRef = useRef<HTMLDivElement>(null);
		const scheduleRef = useRef<HTMLDivElement>(null);
		const { inputConfig } = useContext(AppInputConfigContext);

		const { account } = useContext(UserContext);
		const {
			refetchCustomerProfiles,
			refetchLocations,
			refetchServiceAreas,
		} = useContext(AccountConfigContext);
		const { refetch: refetchCatalogs, catalogs } = useCatalogsContext();
		const {
			selectedTemplateIds,
			setSelectedTemplateIds,
			setSelectTemplateModalOpen,
		} = useContext(TemplateContext);
		const [scrappedDataLoading, setScrappedDataLoading] = useState(false);
		const tonesOptions = inputConfig?.['Tone'] ?? [];
		const audienceOptions = inputConfig?.['Audience'] ?? [];
		const currentOpenSectionIndex = openSections.findIndex((isOpen) => isOpen);
		const { fetchConfig: fetchTones } = useAccountConfigOptions('Tone');
		const { fetchConfig: fetchAudiences } = useAccountConfigOptions('Audience');
		const [hasLoadedInitialPlacements, setHasLoadedInitialPlacements] =
			useState(false);
		const [selectedPlacements, setSelectedPlacements] = useState<string[]>([]);
		const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
		const [forcedOpenSections, setForcedOpenSections] = useState<
			boolean[] | undefined
		>(undefined);
		const [forcedActiveSection, setForcedActiveSection] = useState<
			number | undefined
		>(undefined);
		const [preventScroll, setPreventScroll] = useState(false);
		const [userClickedStep3, setUserClickedStep3] = useState(false);
		const [isGeneratingBudget, setIsGeneratingBudget] = useState(false);
		const [isBudgetLoading, setIsBudgetLoading] = useState(false);
		const [hasInitialCopilotNavigation, setHasInitialCopilotNavigation] =
			useState(false);

		// Onboarding state for Campaign Wizard (Wizard 2)
		const location = useLocation();
		const navigate = useNavigate();
		const { id: routeParamId } = useParams<{ campaignId: string }>();
		const { wizard2Shown, wizard2DismissedManually, showCampaignWizard: contextShowCampaignWizard, closeCampaignWizard, showCampaignFormTour, closeCampaignFormTour, showDesignsTour, designsTourStep, triggerDesignsTour, closeDesignsTour, advanceDesignsTour, setDesignsTourStep, showFinalizeTour, finalizeTourStep, triggerFinalizeTour, closeFinalizeTour, advanceFinalizeTour, setFinalizeTourStep } = useOnboarding();

		// Reset all tours when campaign changes to prevent stale tour state
		const prevCampaignIdRef = useRef<string | undefined>(undefined);
		useEffect(() => {
			if (prevCampaignIdRef.current !== undefined && prevCampaignIdRef.current !== campaignId) {
				// Campaign changed, close all active tours
				closeDesignsTour();
				closeFinalizeTour();
				closeCampaignFormTour();
			}
			prevCampaignIdRef.current = campaignId;
		}, [campaignId, closeDesignsTour, closeFinalizeTour, closeCampaignFormTour]);

		const [localShowCampaignWizard, setLocalShowCampaignWizard] = useState(false);
		const [showStepsOverlay, setShowStepsOverlay] = useState(false);
		const wizardCheckedRef = useRef(false);

		// Combine local and context state for showing the wizard
		const showCampaignWizard = localShowCampaignWizard || contextShowCampaignWizard;

		// Campaign Copilot integration
		const {
			enterCampaignMode,
			setAvailableCampaignContext,
			setOnCampaignFieldUpdate,
			setOnCampaignUIAction,
			sendMessage,
			isCampaignMode,
			setAiTypingField,
			aiTypingField,
		} = useCopilotChatContext();

		// Right sidebar control (to actually open the sidebar panel)
		const { handleOpenSidebar } = useRightSidebarContext();

		// Ref to store the form's setValue function for external updates
		const formSetValueRef = useRef<((field: string, value: any, options?: { shouldDirty?: boolean }) => void) | null>(null);

		// Ref to store the function to open the product modal from GatherInfoForm
		const openProductModalRef = useRef<((prefill?: { name?: string; description?: string; type?: string }) => void) | null>(null);
		// Refs for customer profile, location, and service area modals
		const openCustomerProfileModalRef = useRef<((prefill?: Record<string, unknown>) => void) | null>(null);
		const openLocationModalRef = useRef<((prefill?: Record<string, unknown>) => void) | null>(null);
		const openServiceAreaModalRef = useRef<((prefill?: Record<string, unknown>) => void) | null>(null);
		// Ref to store the products refetch function from GatherInfoForm
		const refetchProductsRef = useRef<(() => Promise<void>) | null>(null);

		// Callback to receive the form's setValue function from GatherInfoForm
		const handleFormSetValueReady = useCallback((setValueFn: (field: string, value: string) => void) => {
			formSetValueRef.current = setValueFn;
		}, []);

		// Callback to receive the product modal opener from GatherInfoForm
		const handleProductModalOpenerReady = useCallback((openerFn: (prefill?: { name?: string; description?: string; type?: string }) => void) => {
			openProductModalRef.current = openerFn;
		}, []);

		// Callback to receive the customer profile modal opener from GatherInfoForm
		const handleCustomerProfileModalOpenerReady = useCallback((openerFn: (prefill?: Record<string, unknown>) => void) => {
			openCustomerProfileModalRef.current = openerFn;
		}, []);

		// Callback to receive the products refetch function from GatherInfoForm
		const handleProductsRefetchReady = useCallback((refetchFn: () => Promise<void>) => {
			refetchProductsRef.current = refetchFn;
		}, []);

		// Callback to receive the location modal opener from GatherInfoForm
		const handleLocationModalOpenerReady = useCallback((openerFn: (prefill?: Record<string, unknown>) => void) => {
			openLocationModalRef.current = openerFn;
		}, []);

		// Callback to receive the service area modal opener from GatherInfoForm
		const handleServiceAreaModalOpenerReady = useCallback((openerFn: (prefill?: Record<string, unknown>) => void) => {
			openServiceAreaModalRef.current = openerFn;
		}, []);

		// Ref to track the current AI typing animation (to cancel it if user starts typing)
		const aiTypingAnimationRef = useRef<{
			field: string;
			timeoutId: ReturnType<typeof setTimeout> | null;
			cancelled: boolean;
		} | null>(null);

		// Function to cancel any ongoing AI typing animation
		const cancelAiTypingAnimation = useCallback(() => {
			if (aiTypingAnimationRef.current) {
				aiTypingAnimationRef.current.cancelled = true;
				if (aiTypingAnimationRef.current.timeoutId) {
					clearTimeout(aiTypingAnimationRef.current.timeoutId);
				}
				aiTypingAnimationRef.current = null;
				setAiTypingField(null);
			}
		}, [setAiTypingField]);

		// Function to animate typing character by character
		const animateTyping = useCallback((
			field: string,
			targetValue: string,
			onComplete?: () => void
		) => {
			// Cancel any ongoing animation
			cancelAiTypingAnimation();

			// Set the field as being typed by AI
			setAiTypingField(field);

			// Create animation state
			const animationState = {
				field,
				timeoutId: null as ReturnType<typeof setTimeout> | null,
				cancelled: false,
			};
			aiTypingAnimationRef.current = animationState;

			let currentIndex = 0;
			const typingSpeed = 15; // milliseconds per character

			const typeNextChar = () => {
				// Check if animation was cancelled
				if (animationState.cancelled) {
					return;
				}

				if (currentIndex <= targetValue.length) {
					const currentValue = targetValue.substring(0, currentIndex);

					// Update the form value
					if (formSetValueRef.current) {
						formSetValueRef.current(field, currentValue);
					}

					currentIndex++;
					animationState.timeoutId = setTimeout(typeNextChar, typingSpeed);
				} else {
					// Animation complete
					aiTypingAnimationRef.current = null;
					setAiTypingField(null);
					onComplete?.();
				}
			};

			// Start typing
			typeNextChar();
		}, [cancelAiTypingAnimation, setAiTypingField]);

		// Set available campaign context when on a campaign page (enables mode tabs in copilot)
		// Use campaign?.id instead of campaignId because campaignId from context stays as 'new'
		// even after campaign creation (by design in CampaignProvider)
		useEffect(() => {
			const realCampaignId = campaign?.id;
			if (realCampaignId) {
				setAvailableCampaignContext({
					campaignId: realCampaignId,
					campaignTitle: campaignTitle || campaign?.title || 'Campaign',
				});
			}
			// Note: We don't set to null here anymore - that's handled by the CopilotChatProvider's
			// auto-exit logic when navigating away from campaign pages
		}, [campaign?.id, campaignTitle, campaign?.title, setAvailableCampaignContext]);

		// Cleanup when component unmounts (leaving campaign page entirely)
		useEffect(() => {
			return () => {
				setAvailableCampaignContext(null);
			};
		}, [setAvailableCampaignContext]);

		// Helper to update campaign state after field is set
		const updateCampaignState = useCallback((field: string, value: string) => {
			setCampaign((prev: ICampaign | null) => {
				if (!prev) return prev;

				const updated = { ...prev };

				switch (field) {
					case 'title':
						updated.title = value;
						setCampaignTitle(value);
						break;
					case 'description':
						updated.description = value;
						break;
					case 'destination':
						updated.destination = value;
						break;
					case 'customerProfiles':
						try {
							const profileIds = JSON.parse(value);
							if (Array.isArray(profileIds)) {
								updated.customerProfiles = profileIds;
							}
						} catch (e) {
							console.error('[CreateEditCampaign] Failed to parse customerProfiles:', e);
							return prev;
						}
						break;
					case 'locations':
						try {
							const locationIds = JSON.parse(value);
							if (Array.isArray(locationIds)) {
								updated.locations = locationIds;
							}
						} catch (e) {
							console.error('[CreateEditCampaign] Failed to parse locations:', e);
							return prev;
						}
						break;
					case 'serviceAreas':
						try {
							const serviceAreaIds = JSON.parse(value);
							if (Array.isArray(serviceAreaIds)) {
								updated.serviceAreas = serviceAreaIds;
							}
						} catch (e) {
							console.error('[CreateEditCampaign] Failed to parse serviceAreas:', e);
							return prev;
						}
						break;
					case 'promotedObject':
						// promotedObject is { type: 'catalogs', ids: [catalogId] }
						// The value passed is just the catalog ID string
						updated.promotedObject = {
							type: 'catalogs',
							ids: [value],
						};
						break;
					default:
						console.warn('[CreateEditCampaign] Unknown field:', field);
						return prev;
				}

				return updated;
			});
		}, [setCampaign]);

		// Handle real-time campaign field updates from AI
		const handleCampaignFieldUpdate = useCallback(
			(field: string, value: string) => {

				// Text fields get typing animation
				const textFields = ['title', 'description'];
				// Fields that get a highlight flash effect (non-text fields)
				const flashFields = ['customerProfiles', 'locations', 'serviceAreas', 'destination'];

				if (textFields.includes(field)) {
					// Get current value to check if it's actually different
					const currentValue = campaign?.[field as keyof typeof campaign] as string | undefined;

					// Only animate if the value is actually different
					if (currentValue === value) {
						console.log(`[CreateEditCampaign] Skipping animation for ${field} - value unchanged`);
						return;
					}

					// Use typing animation for text fields
					animateTyping(field, value, () => {
						// Update campaign state after animation completes
						updateCampaignState(field, value);
					});
				} else if (field === 'promotedObject') {
					// Special handling for promotedObject - value is JSON with { id, name, type }
					setAiTypingField(field);

					try {
						const catalogData = JSON.parse(value);
						console.log('[CreateEditCampaign] Updating promotedObject:', catalogData);

						// Update campaign state first
						setCampaign((prev: ICampaign | null) => {
							if (!prev) return prev;
							return {
								...prev,
								promotedObject: {
									type: 'catalogs',
									ids: [catalogData.id],
								},
							};
						});

						// Refetch products FIRST, wait for it, THEN set form values
						// This ensures the new catalog is in the options list before we try to select it
						const doUpdate = async () => {
							if (refetchProductsRef.current) {
								console.log('[CreateEditCampaign] Refetching products...');
								await refetchProductsRef.current();
								console.log('[CreateEditCampaign] Products refetched, setting form values...');
							}

							// Small delay to ensure React has processed the refetch state update
							await new Promise(resolve => setTimeout(resolve, 100));

							// Now set form values after products are refreshed
							if (formSetValueRef.current) {
								// Set the type first
								formSetValueRef.current('promotedObject.type', 'catalogs', { shouldDirty: true });
								// Set the value as array of options
								formSetValueRef.current('promotedObject.value', [
									{ value: catalogData.id, label: catalogData.name }
								], { shouldDirty: true });
								console.log('[CreateEditCampaign] Form values set:', catalogData);
							}
						};

						doUpdate();
					} catch (e) {
						console.error('[CreateEditCampaign] Failed to parse promotedObject:', e);
					}

					// Clear the highlight after 2 seconds
					setTimeout(() => {
						setAiTypingField(null);
					}, 2000);
				} else if (flashFields.includes(field)) {
					// For collection fields (customerProfiles, locations, serviceAreas), show flash effect
					setAiTypingField(field);

					// Update the form and campaign state
					if (formSetValueRef.current) {
						formSetValueRef.current(field, value);
					}
					updateCampaignState(field, value);

					// Refetch the corresponding data from the server to ensure UI is up to date
					// This is necessary when AI creates new items (customer profiles, locations, service areas, products)
					if (field === 'customerProfiles') {
						refetchCustomerProfiles();
					} else if (field === 'locations') {
						refetchLocations();
					} else if (field === 'serviceAreas') {
						refetchServiceAreas();
					}

					// Clear the highlight after 2 seconds (only if still showing this field)
					const currentField = field;
					setTimeout(() => {
						// Only clear if it's still the same field being highlighted
						setAiTypingField(null);
					}, 2000);
				} else {
					// For other fields, set value directly
					if (formSetValueRef.current) {
						formSetValueRef.current(field, value);
					}
					// Update campaign state immediately
					updateCampaignState(field, value);
				}
			},
			[animateTyping, updateCampaignState, setAiTypingField, campaign, refetchCustomerProfiles, refetchLocations, refetchServiceAreas, setCampaign]
		);

		// Handle UI actions from Campaign Copilot (e.g., opening product modal)
		const handleCampaignUIAction = useCallback(
			(action: string, data?: Record<string, unknown>) => {

				if (action === 'open_product_modal') {
					// Open the campaign form's product modal with prefill data
					if (openProductModalRef.current) {
						openProductModalRef.current({
							name: data?.prefill_name as string | undefined,
							description: data?.prefill_description as string | undefined,
							type: data?.prefill_type as string | undefined,
						});
					} else {
						console.warn('[CreateEditCampaign] Product modal opener not available');
					}
				} else if (action === 'open_customer_profile_modal') {
					// Open the customer profile modal with prefill data
					if (openCustomerProfileModalRef.current) {
						const customerProfile = data?.customerProfile as Record<string, unknown> | undefined;
						openCustomerProfileModalRef.current(customerProfile);
					} else {
						console.warn('[CreateEditCampaign] Customer profile modal opener not available');
					}
				} else if (action === 'open_location_modal') {
					// Open the location modal with prefill data
					if (openLocationModalRef.current) {
						const location = data?.location as Record<string, unknown> | undefined;
						openLocationModalRef.current(location);
					} else {
						console.warn('[CreateEditCampaign] Location modal opener not available');
					}
				} else if (action === 'open_service_area_modal') {
					// Open the service area modal with prefill data
					if (openServiceAreaModalRef.current) {
						const serviceArea = data?.serviceArea as Record<string, unknown> | undefined;
						openServiceAreaModalRef.current(serviceArea);
					} else {
						console.warn('[CreateEditCampaign] Service area modal opener not available');
					}
				}
			},
			[]
		);

		// Register the callbacks with the copilot context
		useEffect(() => {
			setOnCampaignFieldUpdate(handleCampaignFieldUpdate);
			setOnCampaignUIAction(handleCampaignUIAction);

			// Cleanup when unmounting
			return () => {
				setOnCampaignFieldUpdate(undefined);
				setOnCampaignUIAction(undefined);
			};
		}, [handleCampaignFieldUpdate, setOnCampaignFieldUpdate, handleCampaignUIAction, setOnCampaignUIAction]);

		// Handler for when AI triggers product creation and user saves
		// Sends a confirmation message to the chat
		const handleProductCreatedByAI = useCallback(
			(catalogId: string, catalogName: string) => {
				// Only send message if we're in campaign copilot mode
				if (isCampaignMode) {
					// Send a user message (as if the user typed it) to confirm the action
					// This will trigger the AI to respond with a confirmation
					sendMessage(`I've added "${catalogName}" as the product/service for this campaign.`, 'user', true);
				}
				// Trigger highlight effect on promotedObject field
				setAiTypingField('promotedObject');
				setTimeout(() => setAiTypingField(null), 2000);
			},
			[isCampaignMode, sendMessage, setAiTypingField]
		);

		// Handler for when customer profile is saved after being opened by AI
		const handleCustomerProfileSavedByAI = useCallback(
			(profileName: string) => {
				// Trigger highlight effect on customerProfiles field
				setAiTypingField('customerProfiles');
				setTimeout(() => setAiTypingField(null), 2000);
				// Send confirmation message if in campaign mode
				if (isCampaignMode) {
					sendMessage(`I've saved the customer profile "${profileName}" to this campaign.`, 'user', true);
				}
			},
			[isCampaignMode, sendMessage, setAiTypingField]
		);

		// Handler for when location is saved after being opened by AI
		const handleLocationSavedByAI = useCallback(
			(locationName: string) => {
				// Trigger highlight effect on locations field
				setAiTypingField('locations');
				setTimeout(() => setAiTypingField(null), 2000);
				// Send confirmation message if in campaign mode
				if (isCampaignMode) {
					sendMessage(`I've updated the locations for this campaign.`, 'user', true);
				}
			},
			[isCampaignMode, sendMessage, setAiTypingField]
		);

		// Handler for when service area is saved after being opened by AI
		const handleServiceAreaSavedByAI = useCallback(
			(areaName: string) => {
				// Trigger highlight effect on serviceAreas field
				setAiTypingField('serviceAreas');
				setTimeout(() => setAiTypingField(null), 2000);
				// Send confirmation message if in campaign mode
				if (isCampaignMode) {
					sendMessage(`I've updated the service areas for this campaign.`, 'user', true);
				}
			},
			[isCampaignMode, sendMessage, setAiTypingField]
		);

		// Generate auto-message based on field name
		const getAutoMessageForField = (fieldName: string): string => {
			const fieldMessages: Record<string, string> = {
				title: 'I need help with my campaign name',
				description: 'I need help describing my campaign',
				destination: 'I need help with my destination URL',
				promotedObject: 'I need help with what I want to promote',
				product: 'I need help with what I want to promote',
				customerProfiles: 'I need help defining my target customers',
				locations: 'I need help setting up my business locations',
				serviceAreas: 'I need help defining my service areas',
			};
			return fieldMessages[fieldName] || `I need help with the ${fieldName} field`;
		};

		// Handler for when DD editing is complete (from chat deep-link)
		const handleDDEditComplete = useCallback(() => {
			if (returnToChat) {
				// Navigate back to previous page or home, the chat will remain open
				navigate(-1);
			}
		}, [returnToChat, navigate]);

		// Handler to open Campaign Copilot for a specific field
		const handleOpenCampaignCopilot = useCallback(
			async (fieldName: string) => {
				// Close the campaign form tour if it's active (user chose to use AI instead)
				closeCampaignFormTour();

				// Check campaign.id instead of campaignId because campaignId from context
				// stays as 'new' even after campaign creation (by design in CampaignProvider)
				const existingCampaignId = campaign?.id;

				// Generate auto-message for the field
				const autoMessage = getAutoMessageForField(fieldName);

				if (existingCampaignId) {
					// Existing campaign - enter campaign mode directly
					// Open the sidebar first, then enter campaign mode
					// This ensures the panel is open and will re-render with campaign data
					handleOpenSidebar('copilot');
					await enterCampaignMode(existingCampaignId, campaignTitle || campaign?.title || 'Campaign', fieldName, autoMessage);
				} else {
					// New campaign - create empty campaign first to get campaignId for chat history
					try {
						const newCampaign = await createOrUpdateCampaign({}, 'new');

						// Update the campaign in context
						setCampaign(newCampaign);

						// Navigate to the new campaign URL (replaces /campaigns/new with /campaigns/{id})
						navigate(`/projects/campaigns/${newCampaign.id}`, { replace: true });

						// Open the sidebar first, then enter campaign mode
						// This ensures the panel is open and will re-render with campaign data
						handleOpenSidebar('copilot');
						await enterCampaignMode(newCampaign.id, newCampaign.title || 'New Campaign', fieldName, autoMessage);
					} catch (error) {
						console.error('[CreateEditCampaign] Failed to create campaign for AI chat:', error);
						toastError(error);
						// Fallback: just open copilot sidebar without campaign context
						handleOpenSidebar('copilot');
					}
				}
			},
			[campaign?.id, campaign?.title, campaignTitle, enterCampaignMode, handleOpenSidebar, setCampaign, navigate, closeCampaignFormTour]
		);

		// Check if this is a new campaign route
		const isNewCampaignRoute = campaignId === 'new' || location.pathname.endsWith('/campaigns/new');

		// Debug: Log on every render for Wizard 2

		// ALL useEffect hooks must be called here, before any conditional logic
		const prevCampaignRef = useRef(campaign);
		const selectedTemplateIdsRef = useRef<string[]>([]);
		const previousPlacementsRef = useRef(campaign?.placements);
		const hasFirstUpdateOccurred = useRef(false);
		const autoBreakdownInitializedRef = useRef<Set<string>>(new Set()); // Track which campaigns have had auto breakdown initialized
		const isInitializingCampaignRef = useRef(true); // Track if we're still in initialization phase

		// Memoize context values to prevent unnecessary re-renders
		const contextValues = useMemo(
			() => ({
				campaignId,
				campaign: campaign
					? {
							id: campaign.id,
							status: campaign.status,
							designDirectionsCount: campaign.designDirections?.length,
							budget: campaign.budget,
						}
					: null,
				enabledSections,
				openSections,
				isCampaignFetching,
			}),
			[
				campaignId,
				campaign?.id,
				campaign?.status,
				campaign?.designDirections?.length,
				campaign?.budget,
				enabledSections,
				openSections,
				isCampaignFetching,
			],
		);

		// Show campaign wizard modal on first visit to new campaign page (Wizard 2)
		useEffect(() => {
			const searchParams = new URLSearchParams(location.search);
			const fromShopify = searchParams.get('fromShopify') === 'true';
			const fromWizard = searchParams.get('fromWizard') === 'true';
			const fromWordPress = searchParams.get('fromWordPress') === 'true';
			const hasPrefillCatalogId = searchParams.get('prefillCatalogId') !== null;
			const hasStatusDraft = searchParams.get('status') === 'draft';

			// If coming from Shopify/Wizard/WordPress or campaign already has draft status, mark as checked and skip wizard
			if (fromShopify || fromWizard || fromWordPress || hasPrefillCatalogId || hasStatusDraft) {
				wizardCheckedRef.current = true;
				return;
			}

			if (!wizard2Shown && isNewCampaignRoute && !wizardCheckedRef.current) {
				wizardCheckedRef.current = true;
				setTimeout(() => {
					setLocalShowCampaignWizard(true);
				}, 500);
			}
		}, [wizard2Shown, isNewCampaignRoute]);

		// When CampaignFormTour starts, expand Step 1 and collapse other steps
		// Skip when coming from copilot - user should have free navigation
		useEffect(() => {
			if (fromCopilot) return;
			if (showCampaignFormTour && wizard2DismissedManually) {
				setOpenSections([true, false, false, false]);
			}
		}, [showCampaignFormTour, wizard2DismissedManually, setOpenSections, fromCopilot]);

		// When DesignsTour is active, control which step accordion is open based on tour step
		// EXCEPT for the final step (FINAL_EXPLANATION) where user can navigate freely
		// Skip when coming from copilot - user should have free navigation
		useEffect(() => {
			if (fromCopilot) return;
			if (showDesignsTour && wizard2DismissedManually) {
				// Don't force accordion state on final step - user can navigate freely
				if (designsTourStep === DESIGNS_TOUR_STEPS.FINAL_EXPLANATION) {
					return;
				}
				// Step 0 (GENERATE_DESIGNS_BUTTON) - the button is inside Step 1, so keep Step 1 open
				if (designsTourStep === DESIGNS_TOUR_STEPS.GENERATE_DESIGNS_BUTTON) {
					setOpenSections([true, false, false, false]);
				} else {
					// Other steps are in Step 2 (Generate Designs accordion)
					setOpenSections([false, true, false, false]);
				}
			}
		}, [showDesignsTour, wizard2DismissedManually, designsTourStep, setOpenSections, fromCopilot]);

		// When FinalizeTour is active, keep Step 3 (Finalize) accordion open
		// EXCEPT for the final step (PUBLISH_BUTTON) where user can navigate freely
		// Skip when coming from copilot - user should have free navigation
		useEffect(() => {
			if (fromCopilot) return;
			if (showFinalizeTour && wizard2DismissedManually) {
				// Don't force accordion state on final step - user can navigate freely
				if (finalizeTourStep === FINALIZE_TOUR_STEPS.PUBLISH_BUTTON) {
					return;
				}
				// Other Finalize Tour steps (Schedule, Budget) are in Step 3 (Finalize)
				setOpenSections([false, false, true, false]);
			}
		}, [showFinalizeTour, wizard2DismissedManually, finalizeTourStep, setOpenSections, fromCopilot]);

		const handleCampaignWizardClose = useCallback((showOverlay?: boolean) => {
			// Close both local and context wizard states
			setLocalShowCampaignWizard(false);
			closeCampaignWizard();
			// Only show steps overlay for manual option (when showOverlay is true)
			if (showOverlay) {
				setShowStepsOverlay(true);
			}
		}, [closeCampaignWizard]);

		// Handle product selection from wizard modal
		const handleWizardProductSelect = useCallback((product: any) => {
			// Navigate to campaign with prefill params
			const productId = product.id || product._id;
			navigate(`/projects/campaigns/new?fromWizard=true&prefillCatalogId=${productId}`, {
				replace: true,
			});
		}, [navigate]);

		const handleStepsOverlayDismiss = useCallback(() => {
			setShowStepsOverlay(false);
		}, []);

		// Handler for when the campaign form tour is completed or skipped
		const handleFormTourComplete = useCallback(() => {
			closeCampaignFormTour();
			// Start the Designs Tour (Step 2) after a small delay
			setTimeout(() => {
				triggerDesignsTour();
			}, 300);
		}, [closeCampaignFormTour, triggerDesignsTour]);

		const handleFormTourSkip = useCallback(() => {
			closeCampaignFormTour();
			// Note: Do NOT trigger DesignsTour here - user explicitly closed/skipped the tour
		}, [closeCampaignFormTour]);

		// Handler for when the designs tour is completed or skipped
		const handleDesignsTourComplete = useCallback(() => {
			closeDesignsTour();
			// Start the Finalize Tour (Step 3) after a small delay
			setTimeout(() => {
				triggerFinalizeTour();
			}, 300);
		}, [closeDesignsTour, triggerFinalizeTour]);

		const handleDesignsTourSkip = useCallback(() => {
			closeDesignsTour();
			// Note: Do NOT trigger FinalizeTour here - user explicitly closed/skipped the tour
		}, [closeDesignsTour]);

		// Handler for when the finalize tour is completed or skipped
		const handleFinalizeTourComplete = useCallback(() => {
			closeFinalizeTour();
		}, [closeFinalizeTour]);

		const handleFinalizeTourSkip = useCallback(() => {
			closeFinalizeTour();
		}, [closeFinalizeTour]);

		// Track campaign changes
		useEffect(() => {
			if (prevCampaignRef.current !== campaign) {
				prevCampaignRef.current = campaign;
				// Sync local state with campaign context
				if (campaign?.designDirections) {
					setDesignDirections(campaign.designDirections);
				}
				if (campaign?.creatives) {
					setCreatives(campaign.creatives);
				}
			}
		}, [campaign]);

		// Initialize tones and audiences
		useEffect(() => {
			fetchTones();
			fetchAudiences();
			return () => {
				setSelectedTemplateIds([]);
			};
		}, []); // Removed fetchTones and fetchAudiences from dependencies

		// Update designDirections and creatives when campaign changes
		useEffect(() => {
			if (campaign) {
				unstable_batchedUpdates(() => {
					setDesignDirections(campaign.designDirections ?? []);
					setCreatives(campaign.creatives ?? []);
				});
			}
		}, [campaign?.id, campaign?.designDirections, campaign?.creatives]);

		// Process fusion events
		useEffect(() => {
			const processEvent = (event: IEvent<any>) => {
				if (event.target == 'campaign') {
					if (event.action == 'setNavigation') {
						const navigationEvent = event as IEvent<ICampaignNavigationEvent>;
						const sections = navigationEvent.value.sections;
						const activeSection = navigationEvent.value.activeSection;
						const preventScrollFlag = navigationEvent.value.preventScroll;
						unstable_batchedUpdates(() => {
							setForcedOpenSections(sections);
							setForcedActiveSection(activeSection);
							setPreventScroll(preventScrollFlag || false);
						});
					}
				}
			};

			if (window.fusion && Array.isArray(window.fusion)) {
				while (window.fusion.length) {
					processEvent(window.fusion.pop());
				}
			}
			window.fusion = { push: processEvent };
		}, []);

		// Handle sections and navigation
		useEffect(() => {
			if (
				!campaign?.placements &&
				campaignId === 'new' &&
				availableChannelGroups?.length
			) {
				const defaultPlacements = availableChannelGroups.flatMap((group) =>
					group.channels.flatMap(
						(channel) =>
							channel.placements?.map((placement) => placement.id) || [],
					),
				);
				setSelectedPlacements(defaultPlacements);
			}

			// Skip accordion logic when onboarding tours are active - they control the accordions
			// EXCEPT for final steps where user can navigate freely
			const isDesignsTourControlling = showDesignsTour && designsTourStep !== DESIGNS_TOUR_STEPS.FINAL_EXPLANATION;
			const isFinalizeTourControlling = showFinalizeTour && finalizeTourStep !== FINALIZE_TOUR_STEPS.PUBLISH_BUTTON;
			if ((isDesignsTourControlling || isFinalizeTourControlling) && wizard2DismissedManually) {
				return;
			}

			// Skip accordion logic when coming from copilot
			// Let user navigate freely without auto-forcing sections based on campaign state
			if (fromCopilot) {
				return;
			}

			const highestEnabledIndex = enabledSections
				.map((enabled, index) => (enabled ? index : -1))
				.reduce((max, current) => (current > max ? current : max), -1);

			// Determine which section should be open based on campaign state
			let sectionToOpen = highestEnabledIndex;

			// Always open Step 2 when there are design directions unless user explicitly clicked Step 3
			if (campaign?.designDirections?.length && !userClickedStep3) {
				sectionToOpen = 1; // Step 2
			}

			if (forcedOpenSections) {
				setOpenSections(forcedOpenSections);

				// If Step 3 is being opened via forcedOpenSections, set userClickedStep3 to true
				if (forcedOpenSections[2]) {
					setUserClickedStep3(true);
				}

				// Reset forcedOpenSections after applying it so normal logic can take over
				setTimeout(() => {
					setForcedOpenSections(undefined);
				}, 100);
			} else {
				setOpenSections((prev) =>
					prev.map((_, index) => index === sectionToOpen),
				);
			}

			const selectedIndex = forcedActiveSection ?? sectionToOpen;

			// Check if we're already on the section we want to open (no scroll needed)
			const currentlyOpenSection = openSections.findIndex(
				(section) => section === true,
			);
			const shouldScroll =
				!preventScroll && selectedIndex !== currentlyOpenSection;

			if (shouldScroll) {
				switch (selectedIndex) {
					case 1:
						scrollIntoRef(designDirectionsRef);
						break;
					case 2:
						scrollIntoRef(scheduleRef);
						break;
					default:
						break;
				}
			}
		}, [
			availableChannelGroups,
			campaignId,
			forcedOpenSections,
			forcedActiveSection,
			preventScroll,
			fromCopilot,
			enabledSections,
			userClickedStep3,
			showDesignsTour,
			designsTourStep,
			showFinalizeTour,
			finalizeTourStep,
			wizard2DismissedManually,
		]);

		// Handle copilot navigation - only runs once when first navigating from copilot
		const hasClosedToursRef = useRef(false);
		const hasRefetchedRef = useRef(false);
		useEffect(() => {
			// When coming from copilot, clear any forcedOpenSections from window.fusion events
			// This prevents the event from interfering with user's accordion navigation
			if (fromCopilot && forcedOpenSections !== undefined) {
				setForcedOpenSections(undefined);
				return;
			}

			// Handle copilot navigation without scroll - this runs when campaign changes
			if (
				fromCopilot &&
				campaign &&
				!hasInitialCopilotNavigation
			) {
				// Force refetch campaign and related data to get fresh data
				// This ensures customer profiles, locations, etc. are displayed correctly when navigating from chat
				if (!hasRefetchedRef.current) {
					hasRefetchedRef.current = true;
					refetchCampaign();
					// Also refetch customer profiles, locations, and service areas from context
					// This fixes the issue where these don't show on first navigation from copilot
					refetchCustomerProfiles();
					refetchLocations();
					refetchServiceAreas();
				}

				// Close all tours when navigating from copilot to allow free accordion navigation
				// Use ref to ensure we only close tours once
				if (!hasClosedToursRef.current) {
					hasClosedToursRef.current = true;
					closeCampaignFormTour();
					closeDesignsTour();
					closeFinalizeTour();
				}

				unstable_batchedUpdates(() => {
					// Always set step 1 open when coming from copilot
					setOpenSections([true, false, false, false]);
					setHasInitialCopilotNavigation(true);
				});

				// Scroll to top of page with proper timing
				setTimeout(() => {
					window.scrollTo({ top: 0, behavior: 'smooth' });
				}, 100);

				// Auto-open Campaign Copilot tab when coming from copilot
				// This ensures the user sees the campaign-specific chat instead of Fusion Copilot
				setTimeout(async () => {
					handleOpenSidebar('copilot');
					await enterCampaignMode(
						campaign.id,
						campaignTitle || campaign?.title || 'Campaign'
					);
				}, 300);

				return;
			}

			// The openSections are now handled by the CampaignProvider
			// Only override if forcedOpenSections is provided and not coming from copilot
			if (!fromCopilot && forcedOpenSections !== undefined) {
				setOpenSections(forcedOpenSections);
			}
		}, [
			campaign?.id,
			fromCopilot,
			forcedOpenSections,
			hasInitialCopilotNavigation,
			handleOpenSidebar,
			enterCampaignMode,
			campaignTitle,
			campaign?.title,
			closeCampaignFormTour,
			closeDesignsTour,
			closeFinalizeTour,
			refetchCampaign,
			refetchCustomerProfiles,
			refetchLocations,
			refetchServiceAreas,
		]);

		// Load initial placements and group
		useEffect(() => {
			if (campaign && !hasLoadedInitialPlacements) {
				unstable_batchedUpdates(() => {
					if (campaign.placements) {
						setSelectedPlacements(campaign.placements);
					}
					if (campaign.group) {
						setGroup(campaign.group);
					}
					setHasLoadedInitialPlacements(true);
				});
			}
		}, [campaign?.id, hasLoadedInitialPlacements]);

		// Load channels
		useEffect(() => {
			const getChannels = async () => {
				if (!account?.id) return;
				setIsLoadingChannels(true);
				const { channels, creatives } = await getAvailableChannels(account.id);
				unstable_batchedUpdates(() => {
					setAvailableChannelGroups(channels);
					setAvailableCreatives(creatives);
					setIsLoadingChannels(false);
				});
			};
			getChannels();
		}, [account?.id]);

		// Calculate budget breakdown when availableChannelGroups loads for campaigns with budget but no breakdown
		useEffect(() => {
			const campaignKey = campaign?.id || 'new';

			if (
				availableChannelGroups &&
				campaign?.budget &&
				campaign.budget.total > 0 &&
				(!campaign.budget.breakdown ||
					campaign.budget.breakdown.length === 0) &&
				!isGeneratingBudget &&
				!autoBreakdownInitializedRef.current.has(campaignKey) // Only initialize once per campaign
			) {
				// Mark this campaign as having had auto breakdown initialized
				autoBreakdownInitializedRef.current.add(campaignKey);
				setIsGeneratingBudget(true);

				updateBudget({
					total: campaign.budget.total,
					placements: campaign.placements,
					availableChannelGroups: availableChannelGroups,
					source: 'Auto breakdown on channels load',
				})
					.catch((error) => {
						toastError(error);
					})
					.finally(() => {
						setIsGeneratingBudget(false);
					});
			}
		}, [
			availableChannelGroups,
			campaign?.id, // Only depend on campaign ID, not the entire budget object
			isGeneratingBudget,
		]);

		// Update selectedTemplateIds ref
		useEffect(() => {
			selectedTemplateIdsRef.current = selectedTemplateIds;
		}, [selectedTemplateIds]);

		// Reset initialization flag when campaign changes
		useEffect(() => {
			isInitializingCampaignRef.current = true;
		}, [campaignId]);

		// Mark initialization as complete when campaign and channel groups are loaded
		useEffect(() => {
			if (
				campaign &&
				availableChannelGroups &&
				availableChannelGroups.length > 0
			) {
				// Wait a bit to ensure all initial updates have completed
				const timer = setTimeout(() => {
					isInitializingCampaignRef.current = false;
				}, 1000); // 1 second delay

				return () => clearTimeout(timer);
			}
		}, [campaign, availableChannelGroups]);

		// Handle placements changes
		useEffect(() => {
			const currentPlacementsString = JSON.stringify(campaign?.placements);
			const previousPlacementsString = JSON.stringify(
				previousPlacementsRef.current,
			);

			if (currentPlacementsString !== previousPlacementsString) {
				if (!hasFirstUpdateOccurred.current) {
					hasFirstUpdateOccurred.current = true;
				} else {
					// Only update budget if:
					// 1. Campaign is not new
					// 2. Budget already exists with breakdown (not during initial setup)
					// 3. We're not currently generating budget
					// 4. Placements actually changed in a meaningful way (length or content)
					// 5. Auto breakdown has already been initialized for this campaign
					const campaignKey = campaign?.id || 'new';
					const placementsLengthChanged =
						(campaign?.placements?.length || 0) !==
						(previousPlacementsRef.current?.length || 0);
					const placementsContentChanged =
						currentPlacementsString !== previousPlacementsString;
					const hasBreakdown = (campaign?.budget?.breakdown?.length || 0) > 0;

					if (
						campaign?.id !== 'new' &&
						campaign?.budget &&
						hasBreakdown && // Only if budget breakdown already exists
						!isGeneratingBudget &&
						!isInitializingCampaignRef.current && // Only after initialization is complete
						(placementsLengthChanged || placementsContentChanged) &&
						previousPlacementsRef.current // Only if we had previous placements (not initial load)
					) {
						updateCampaignBudget();
					}
				}

				previousPlacementsRef.current = campaign?.placements;
			}
		}, [campaign?.placements, campaign?.id, isGeneratingBudget]);

		// Generate budget when Step 3 is opened
		useEffect(() => {
			if (
				openSections[2] &&
				userClickedStep3 &&
				campaign &&
				campaign.budget &&
				(!campaign.budget.breakdown ||
					campaign.budget.breakdown.length === 0) &&
				availableChannelGroups &&
				!isGeneratingBudget
			) {
				unstable_batchedUpdates(() => {
					setIsGeneratingBudget(true);
					setIsBudgetLoading(true);
				});
				try {
					// Use centralized budget manager to calculate breakdown
					updateBudget({
						total: campaign.budget.total,
						placements: campaign.placements,
						availableChannelGroups: availableChannelGroups || undefined,
						source: 'Step3 breakdown initialization',
					}).catch((error) => {
						toastError(error);
					});
				} catch (error: any) {
					toastError(error);
				} finally {
					unstable_batchedUpdates(() => {
						setIsGeneratingBudget(false);
						setIsBudgetLoading(false);
					});
				}
			}
		}, [
			openSections[2],
			userClickedStep3,
			campaign?.id,
			availableChannelGroups,
			campaignId,
			isGeneratingBudget,
		]);

		// Fetch old campaign copy
		useEffect(() => {
			if (!campaignId || campaignId === 'new') return;
			const fetchCampaign = async () => {
				try {
					// Use campaign from context instead of making a separate API call
					if (campaign) {
						setOldCampaignCopy(cloneDeepWith(campaign));
					}
				} catch (error) {}
			};

			fetchCampaign();
		}, [campaignId, campaign?.id, setOldCampaignCopy]);

		// Load initial data - simplified to only fetch tones and audiences
		useEffect(() => {
			const loadData = async () => {
				try {
					await fetchTones();
					await fetchAudiences();
					if (account?.id) {
						setIsLoadingChannels(true);
						const { channels, creatives } = await getAvailableChannels(
							account.id,
						);
						unstable_batchedUpdates(() => {
							setAvailableChannelGroups(channels);
							setAvailableCreatives(creatives);
							setIsLoadingChannels(false);
						});
					}
				} catch (error) {
					toastError(error);
				}
			};

			loadData();
		}, [account?.id, campaignId]);

		// NOW ALL HOOKS ARE CALLED, WE CAN CONTINUE WITH THE REST OF THE LOGIC
		const compareCampaigns = useCallback(
			(newCampaign: any, oldCampaign: any, fieldsToOmit: string[]) => {
				const newCampaignCleaned = omit(newCampaign, fieldsToOmit);
				const oldCampaignCleaned = omit(oldCampaign, fieldsToOmit);
				return isEqual(newCampaignCleaned, oldCampaignCleaned);
			},
			[],
		);

		const updateCampaignBudget = useCallback(
			async (schedule?: ICampaignSchedule) => {
				if (!campaignId || !campaign || !campaign.budget) return;

				try {
					// Use centralized budget manager for budget updates
					await updateBudget({
						total: campaign.budget.total,
						placements: campaign.placements,
						availableChannelGroups: availableChannelGroups || undefined,
						source: 'updateCampaignBudget - placements change',
					});

					// If we also need to update schedule, do a separate API call
					if (schedule) {
						const effectiveCampaignId =
							campaignId === 'new' && campaign?.id ? campaign.id : campaignId;

						await createOrUpdateCampaign({ schedule }, effectiveCampaignId);
					}
				} catch (error: any) {
					toastError(error);
				}
			},
			[
				campaignId,
				campaign?.placements,
				campaign?.budget,
				availableChannelGroups,
				updateBudget,
			],
		);

		const availablePlacements = useMemo(
			() =>
				(availableChannelGroups || []).flatMap(
					(channelGroup) => channelGroup.channels,
				),
			[availableChannelGroups],
		);

		const getCampaignBrief = useCallback(async () => {
			try {
				const { callback } = await getInterpretations(campaign?.id || '');
				const { brief_description } = await processCallback(callback);
				const brief = { title: campaignTitle, description: brief_description };
				return brief;
			} catch (error) {
				console.error(error);
			}
		}, [campaign, campaignTitle]);

		const handleCampaignSubmit = useCallback(
			async (layeredTemplates?: any[]) => {
				if (!campaignId || !campaign) return;

				// Navigate to step 2 when user actively generates design directions
				// This is expected behavior - user wants to see the generation progress
				unstable_batchedUpdates(() => {
					setForcedOpenSections([false, true, false, false]);
					setOpenSections([false, true, false, false]);
				});

				// Delay scroll to allow modal closing animation to complete
				setTimeout(() => {
					scrollIntoRef(designDirectionsRef);
				}, 300);

				unstable_batchedUpdates(() => {
					setIsGeneratingBrief(true);
					setIsGeneratingDesignDirections(true);
				});
				try {
					const brief = await getCampaignBrief();
					// Use actual campaign ID if available instead of 'new'
					const effectiveCampaignId =
						campaignId === 'new' && campaign?.id ? campaign.id : campaignId;

					const updatedCampaign = await createOrUpdateCampaign(
						{ brief },
						effectiveCampaignId,
					);
					unstable_batchedUpdates(() => {
						setCampaign(updatedCampaign);
						setOldCampaignCopy(updatedCampaign);
						setSelectedTemplateIds([]);
					});
					await handleGenerateDesignDirections(layeredTemplates);
				} catch (error: any) {
					toastError(error);
				} finally {
					unstable_batchedUpdates(() => {
						setIsGeneratingBrief(false);
					});
				}
			},
			[
				campaignId,
				campaign,
				// getCampaignBrief,
				setCampaign,
				setOldCampaignCopy,
				setSelectedTemplateIds,
			],
		);

		const handleRegenerateDesignDirections = useCallback(async () => {
			if (!campaignId) return;
			scrollIntoRef(designDirectionsRef);

			for (const designDirection of designDirections ?? []) {
				if (!designDirection.locked) {
					try {
						await regenerateCampaignDesignDirection(
							campaignId,
							designDirection.id,
							{},
						);
					} catch (error) {
						console.error(
							`Error regenerating design direction ${designDirection.id}:`,
							error,
						);
					}
				}
			}
			if (campaign) {
				campaign.designDirections &&
					setDesignDirections(campaign.designDirections);
				campaign.creatives && setCreatives(campaign.creatives);
			}
		}, [campaignId, designDirections, campaign]);

		const handleScheduleAndPublish = useCallback(async () => {
			if (!campaignId) return;

			// If DesignsTour is active on FINAL_EXPLANATION step, close it and trigger FinalizeTour
			if (showDesignsTour && designsTourStep === DESIGNS_TOUR_STEPS.FINAL_EXPLANATION) {
				closeDesignsTour();
				// Start FinalizeTour after a small delay to let the accordion open
				setTimeout(() => {
					triggerFinalizeTour();
				}, 500);
			}

			handleOpenSection(2);

			if (openSections[2]) {
				scrollIntoRef(scheduleRef);
			}

			try {
				if (!campaign) return;
				await genrateCampaignCreatives(campaign, '', true);
			} catch (error) {
				console.error('Error generating creatives:' + error);
			}
		}, [campaignId, showDesignsTour, designsTourStep, closeDesignsTour, triggerFinalizeTour]);

		const handleGenerateDesignDirections = useCallback(
			async (layeredTemplates?: any[]) => {
				if (!campaignId) return;
				try {
					// Only navigate to Step 2 if this is a NEW generation (from Step 1)
					// For regenerations or template changes, keep current step state
					const isNewGeneration =
						layeredTemplates && layeredTemplates.length > 0;

					if (isNewGeneration) {
						// User is actively generating new design directions - show them Step 2
						unstable_batchedUpdates(() => {
							setForcedOpenSections([false, true, false, false]);
							setOpenSections([false, true, false, false]);
						});
						scrollIntoRef(designDirectionsRef);
					}

					if (!campaign) return;
					if (layeredTemplates) {
						await generateDesignDirectionsFromLayers(
							campaign,
							layeredTemplates,
						);
					} else {
						await generateDesignDirectionsWithTemplates(
							campaign,
							selectedTemplateIdsRef.current,
						);
					}
				} catch (error: any) {
					toastError(error);
				}
			},
			[campaignId, campaign],
		);

		const scrollIntoRef = useCallback(
			(ref: RefObject<HTMLDivElement>, offset: number = 400) => {
				if (!ref || !ref.current) return;
				const elementPosition =
					ref.current.getBoundingClientRect().top + window.scrollY;
				const offsetPosition = elementPosition - offset;
				setTimeout(() => {
					window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
				}, 50);
			},
			[],
		);

		const toggleSection = useCallback(
			(index: number) => {
				// Always allow toggling when coming from copilot - tours should not interfere
				if (fromCopilot) {
					setOpenSections((prev) => {
						const newSections = prev.map((_, idx) => idx === index);
						return newSections;
					});
					return;
				}

				// Prevent toggling when onboarding tours are controlling the accordion
				// EXCEPT for final steps where user can navigate freely
				const isDesignsTourControlling = showDesignsTour && designsTourStep !== DESIGNS_TOUR_STEPS.FINAL_EXPLANATION;
				const isFinalizeTourControlling = showFinalizeTour && finalizeTourStep !== FINALIZE_TOUR_STEPS.PUBLISH_BUTTON;
				const isCampaignFormTourControlling = showCampaignFormTour;

				if ((isDesignsTourControlling || isFinalizeTourControlling || isCampaignFormTourControlling) && wizard2DismissedManually) {
					return; // Don't allow toggling during tours
				}

				setOpenSections((prev) => {
					const newSections = prev.map((_, idx) => idx === index);
					return newSections;
				});
			},
			[setOpenSections, showDesignsTour, designsTourStep, showFinalizeTour, finalizeTourStep, showCampaignFormTour, wizard2DismissedManually, fromCopilot],
		);

		const handleOpenTemplateModal = useCallback(async () => {
			setSelectTemplateModalOpen(true);
			// If DesignsTour is active and on step 0, advance to step 1 (Template Gallery Selected)
			if (showDesignsTour && designsTourStep === DESIGNS_TOUR_STEPS.GENERATE_DESIGNS_BUTTON) {
				// Small delay to allow modal to render before showing the tour step
				setTimeout(() => {
					setDesignsTourStep(DESIGNS_TOUR_STEPS.TEMPLATE_GALLERY_SELECTED);
				}, 500);
			}
		}, [setSelectTemplateModalOpen, showDesignsTour, designsTourStep, setDesignsTourStep]);

		const handleSubmitSchedule = useCallback(
			(schedule: ICampaignSchedule) => {
				updateCampaignBudget(schedule);
			},
			[updateCampaignBudget],
		);

		const handleOpenSection = useCallback(
			async (section: number) => {
				// Track when user clicks on steps
				if (section === 2) {
					setUserClickedStep3(true);
				} else if (section === 1) {
					setUserClickedStep3(false);
				}

				const fieldsToOmit = [
					'createdAt',
					'creatives',
					'designDirections',
					'lastUpdatedBy',
					'updatedAt',
					'placements',
					'channels',
					'budget',
					'variants',
				];

				const isStepOneOpen =
					openSections.length === 4 &&
					openSections.every(
						(value, index) => value === [true, false, false, false][index],
					);

				// If oldCampaignCopy is null, treat as unchanged (baseline not captured yet)
				// This happens on initial load before the copy effect runs
				const isCampaignUnchanged = !oldCampaignCopy || compareCampaigns(
					campaign,
					oldCampaignCopy,
					fieldsToOmit,
				);

				if (isCampaignUnchanged) {
					toggleSection(section);
				} else {
					setWasCampaignChanged(true);
					if (!isStepOneOpen) {
						toggleSection(section);
						return;
					}
					const configFormSubmitElement =
						document.getElementById('campaign-generate-designs-btn') ||
						document.getElementById('campaign-config-form-submit-with-lock-modal');
					configFormSubmitElement?.click();
				}
			},
			[
				openSections,
				compareCampaigns,
				campaign,
				oldCampaignCopy,
				toggleSection,
				setWasCampaignChanged,
			],
		);

		const handleMetaOptions = useCallback((options: IOption[]) => {
			if (options.length) {
				setMetaOptions(options);
			}
		}, []);

		const isValidCampaignConfig = useMemo(() => {
			if (!campaign) return false;
			return validateCampaignConfig(campaign, metaOptions ?? []);
		}, [
			campaign?.id,
			campaign?.status,
			campaign?.placements,
			campaign?.brief,
			metaOptions,
		]);

		// Show loading state while fetching campaign data
		if (isCampaignFetching) {
			return (
				<Box p={4}>
					<FusionLoading />
				</Box>
			);
		}

		// If campaignId is undefined, user navigated away from campaigns - don't render
		if (!campaignId) {
			return null;
		}

		// Show error state if no campaign and not a new campaign, but only if not currently fetching
		if (
			!campaign &&
			campaignId &&
			campaignId !== 'new' &&
			!isCampaignFetching
		) {
			return (
				<Box p={4}>
					<div>Campaign not found or failed to load.</div>
				</Box>
			);
		}

		return (
			<Box p={{ base: 2, md: 4 }} pt={{ base: '72px', md: '0px' }} w="full" maxW="1400px" mx="auto">
				<CampaignHeader
					campaignDetail={{
						title: !campaignTitle ? 'New Campaign' : campaignTitle,
						group,
						lastEditTime: campaign?.updatedAt,
						lastEditBy: campaign?.lastUpdatedBy?.name,
					}}
					onGroupChange={(value) => setGroup(value)}
					scrappedDataLoading={scrappedDataLoading}
				/>
				<CampaignStepper
					handleOpenSection={handleOpenSection}
					designDirectionsRef={designDirectionsRef}
					scheduleRef={scheduleRef}
					scrollIntoRef={scrollIntoRef}
					currentOpenSectionIndex={currentOpenSectionIndex}
				/>

				{/* REMOVED: FusionLoading with local isLoading state */}
				<Box display="block" ref={gatherInfoRef} pt={{ base: '80px', md: '100px' }}>
					<CampaignSection
						title="Step 1: Gather Information"
						isOpen={openSections[0]}
						onToggle={() => toggleSection(0)}
						stepIdx={0}
						accordionContent={
							<GatherInfoForm
								group={group}
								availableChannels={availablePlacements}
								availableChannelGroups={availableChannelGroups}
								onCampaignTitleChange={setCampaignTitle}
								scrappedDataLoading={scrappedDataLoading}
								onCampaignSubmit={handleCampaignSubmit}
								onMetaOptionsChange={handleMetaOptions}
								setOpenSections={setOpenSections}
								selectedPlacements={selectedPlacements}
								onTemplateGalleryOpen={handleOpenTemplateModal}
								setSelectedPlacements={setSelectedPlacements}
								designDirections={designDirections}
								setDesignDirections={setDesignDirections}
								setCreatives={setCreatives}
								onRegenerateDesignDirections={handleRegenerateDesignDirections}
								onChannelsChange={setSelectedChannels}
								setScrappedDataLoading={setScrappedDataLoading}
								setIsGeneratingDesignDirections={
									setIsGeneratingDesignDirections
								}
								onOpenCampaignCopilot={handleOpenCampaignCopilot}
								onFormSetValueReady={handleFormSetValueReady}
								onProductModalOpenerReady={handleProductModalOpenerReady}
								onProductCreatedByAI={handleProductCreatedByAI}
								onCustomerProfileSavedByAI={handleCustomerProfileSavedByAI}
								onLocationSavedByAI={handleLocationSavedByAI}
								onServiceAreaSavedByAI={handleServiceAreaSavedByAI}
								onCustomerProfileModalOpenerReady={handleCustomerProfileModalOpenerReady}
								onLocationModalOpenerReady={handleLocationModalOpenerReady}
								onServiceAreaModalOpenerReady={handleServiceAreaModalOpenerReady}
								onProductsRefetchReady={handleProductsRefetchReady}
								aiTypingField={aiTypingField}
								onCancelAiTyping={cancelAiTypingAnimation}
								isFormTourActive={showCampaignFormTour && wizard2DismissedManually}
								isDesignsTourActive={showDesignsTour && wizard2DismissedManually}
							/>
						}
						extraInformation={
							isGeneratingBrief ? (
								<SummaryExtraInformation />
							) : campaign?.brief ? (
								<SummaryExtraInformation />
							) : undefined
						}
						isDisabled={!enabledSections[0]}
					/>
					<Box ref={designDirectionsRef} pt={{ base: '0px', md: '20px' }} />
					<CampaignSection
						title="Step 2: Generate Designs"
						isOpen={openSections[1]}
						stepIdx={1}
						infoMessage="Each design version competes in real-time. We track performance and automatically focus on the winner."
						accordionContent={
							<DesignDirections
								creativesConfig={availableCreatives ?? []}
								isLoading={isGeneratingDesignDirections}
								onSubmit={handleScheduleAndPublish}
								designDirections={designDirections}
								setDesignDirections={setDesignDirections}
								creatives={creatives}
								setCreatives={setCreatives}
								availableChannelGroups={availableChannelGroups ?? []}
								selectedChannels={selectedChannels}
								isGeneratingFromModalShared={isGeneratingDesignDirections}
								setIsGeneratingDesignDirections={
									setIsGeneratingDesignDirections
								}
								initialEditDDId={editDDId || undefined}
								onEditComplete={handleDDEditComplete}
							/>
						}
						onToggle={() => handleOpenSection(1)}
						isDisabled={!enabledSections[1]}
					/>
					<Box ref={scheduleRef} pt={{ base: '0px', md: '20px' }} />
					<CampaignSection
						title="Step 3: Finalize"
						isOpen={openSections[2]}
						stepIdx={2}
						accordionContent={
							<CampaignSchedule
								schedule={campaign?.schedule}
								selectedChannels={selectedChannels}
								onScheduleSubmit={handleSubmitSchedule}
								availableChannelGroups={availableChannelGroups}
								requirementsLabelStyle={{ color: '#718096', fontSize: '14px' }}
								isValidCampaignConfig={isValidCampaignConfig}
								designDirections={designDirections}
								creatives={creatives}
								isBudgetLoading={isBudgetLoading}
							/>
						}
						onToggle={() => handleOpenSection(2)}
						isDisabled={!enabledSections[2] || designDirections.length === 0}
					/>
				</Box>

				{/* Campaign Wizard Modal (Wizard 2) */}
				<CampaignWizardModal
					isOpen={showCampaignWizard}
					onClose={handleCampaignWizardClose}
					products={catalogs}
					onProductSelect={handleWizardProductSelect}
				/>

				{/* Campaign Steps Overlay */}
				<CampaignStepsOverlay
					isVisible={showStepsOverlay}
					onDismiss={handleStepsOverlayDismiss}
				/>

				{/* Campaign Form Guided Tour - only show if user explicitly chose "fill manually" */}
				<CampaignFormTour
					isActive={showCampaignFormTour && wizard2DismissedManually}
					onComplete={handleFormTourComplete}
					onSkip={handleFormTourSkip}
					onOpenAiForField={handleOpenCampaignCopilot}
				/>

				{/* Step 2: Designs Tour - guides user through template selection and creative generation */}
				<DesignsTour
					isActive={showDesignsTour && wizard2DismissedManually}
					currentStep={designsTourStep}
					onComplete={handleDesignsTourComplete}
					onSkip={handleDesignsTourSkip}
					onAdvance={advanceDesignsTour}
					setStep={setDesignsTourStep}
					onCloseTemplateGallery={() => setSelectTemplateModalOpen(false)}
				/>

				{/* Step 3: Finalize Tour - guides user through schedule, budget, and publish */}
				<FinalizeTour
					isActive={showFinalizeTour && wizard2DismissedManually}
					currentStep={finalizeTourStep}
					onComplete={handleFinalizeTourComplete}
					onSkip={handleFinalizeTourSkip}
					onAdvance={advanceFinalizeTour}
					setStep={setFinalizeTourStep}
				/>
			</Box>
		);
	} catch (error: any) {
		console.error('Error rendering CreateEditCampaign:', error);

		// Capture error to PostHog for mobile debugging
		import('posthog-js')
			.then((m) => m.default)
			.then((posthog) => {
				posthog.captureException(error, {
					$exception_message: error?.message || 'Unknown error',
					$exception_type: error?.name || 'Error',
					$exception_stack_trace_raw: error?.stack,
					component: 'CreateEditCampaign',
					url: window.location.href,
					campaignId: window.location.pathname.match(/\/projects\/campaigns\/([^?\/]+)/)?.[1],
				});

				posthog.capture('campaign_page_render_error', {
					error_message: error?.message || 'Unknown error',
					error_name: error?.name || 'Error',
					error_stack: error?.stack,
					page_url: window.location.href,
					campaign_id: window.location.pathname.match(/\/projects\/campaigns\/([^?\/]+)/)?.[1],
				});
			})
			.catch((posthogError) => {
				console.error('Failed to capture error to PostHog:', posthogError);
			});

		return (
			<Box p={4}>
				<div>An unexpected error occurred.</div>
			</Box>
		);
	}
});

CreateEditCampaign.displayName = 'CreateEditCampaign';

const CreateEditCampaignWithProvider = withCampaignProvider(CreateEditCampaign);
const CreateEditCampaignWithAppConfigProvider = withAppConfigProvider(
	CreateEditCampaignWithProvider,
);

export default CreateEditCampaignWithAppConfigProvider;
